import { Matrix4, Vector3 } from 'three';
import { Accessory } from '../objects/Accessory';
import { AccessoriesManager } from '../managers/AccessoriesManager';

export class CollisionDetector {
	constructor(private accessoriesManager: AccessoriesManager) {}

	checkCollisions(accessory: Accessory) {
		if (!accessory.collisionMesh) return;

		const oldCollidedAccessories = accessory.getCollidedAccessories();

		for (const otherAccessory of this.accessoriesManager.accessories) {
			if (otherAccessory.id === accessory.id) continue;
			if (!otherAccessory.collisionMesh) continue;
			if (!this.checkCollisionLayersMatch(accessory, otherAccessory)) continue;

			const isColliding = this.detectCollisions(accessory, otherAccessory);

			if (isColliding) {
				accessory.addCollidedAccessories(otherAccessory.id);
				otherAccessory.addCollidedAccessories(accessory.id);
			}

			if (!isColliding && oldCollidedAccessories.includes(otherAccessory.id)) {
				accessory.removeCollidedAccessories(otherAccessory.id);
				otherAccessory.removeCollidedAccessories(accessory.id);
			}
		}

		this.accessoriesManager.syncCollisions();
	}

	private checkCollisionLayersMatch(
		accessoryA: Accessory,
		accessoryB: Accessory
	) {
		return (
			accessoryA.collisionLayers.some((layer) =>
				accessoryB.collisionLayers.includes(layer)
			) &&
			accessoryB.collisionLayers.some((layer) =>
				accessoryA.collisionLayers.includes(layer)
			)
		);
	}

	private detectCollisions(accessoryA: Accessory, accessoryB: Accessory) {
		const aabbCollision = this.checkAABBCollision(accessoryA, accessoryB);
		if (!aabbCollision) return false;

		const shapeCollision = this.checkShapeCollision(accessoryA, accessoryB);
		return shapeCollision;
	}

	private checkAABBCollision(accessoryA: Accessory, accessoryB: Accessory) {
		return accessoryA.collisionBox.intersectsBox(accessoryB.collisionBox);
	}

	private checkShapeCollision(accessoryA: Accessory, accessoryB: Accessory) {
		if (
			!accessoryA.collisionMesh!.geometry.boundsTree ||
			!accessoryB.collisionMesh!.geometry.boundsTree
		)
			throw new Error('MISSING BOUNDS TREE');

		const meshOne = accessoryA.collisionMesh!,
			meshTwo = accessoryB.collisionMesh!;

		const posA = new Vector3();
		const posB = new Vector3();
		accessoryA.object.getWorldPosition(posA);
		accessoryB.object.getWorldPosition(posB);

		const matrix2to1 = new Matrix4()
			.copy(accessoryA.object.matrixWorld)
			.invert()
			.multiply(accessoryB.object.matrixWorld);

		//@ts-ignore
		const hit = meshOne.geometry.boundsTree!.intersectsGeometry(
			meshTwo.geometry,
			matrix2to1
		);

		return hit;
	}
}
